<!--
* MyAccountView.vue
* -----------------
* This component renders the main "My Account" dashboard, dynamically showing
* user-specific sections (e.g., Orders, Profile, Mailbox, etc.) based on:
*
* - Active tenant (country context)
* - User role (staff, dealer, wholesaler, etc.)
* - Feature flags and permissions (via TokenService or tenant config)
*
* It leverages:
* - A central `modulesMatrix` config to define item availability
* - The `useItemsConditions()` composable to encapsulate all visibility logic
* - i18n keys for all text content
*
* The goal is to make this view fully dynamic and scalable for any tenant/role combination,
* while remaining clean, readable, and easy to maintain.
*-->
<template>
  <CategoryTop :title="$t('account.title')" :description="description" />

  <div v-if="loading">
    <Loader :show="true" spinner="spinner-red.svg" />
  </div>
  <MDBRow v-else>
    <MDBCol cols="12" lg="4" class="mb-4" v-for="(item, itemIdx) in accountItems" :key="itemIdx">
      <Card :data="item" :linkText="item.linkText" />
    </MDBCol>
  </MDBRow>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { computed, ref, watchEffect, onMounted } from 'vue'
import { useStore } from 'vuex'

import CategoryTop from '@/components/General/CategoryTop/index.vue'
import Card from '@/components/General/Card/index.vue'
import Loader from '../General/Loader/index.vue'

import { MDBRow, MDBCol } from 'mdb-vue-ui-kit'
import { modulesMatrix } from '@/configs/modulesMatrix'
import { useItemsConditions } from '@/composables/useItemsConditions'

const { t } = useI18n()
const store = useStore()

const description = ref('')
const loading = computed(() => store.getters.getPending)

const {
  tenant,
  getVisibleItems,
  getContext,
} = useItemsConditions()

watchEffect(() => {
  // TODO : refactor it
  const isStaff = store.getters.getMe?.roles?.some(r => r.name === 'staff')
  description.value = isStaff ? t('account.descStaff') : t('account.desc')
})

onMounted(async () => {
  await store.dispatch('me');
})

const accountItems = computed(() => {
  if (!tenant.value) {
    console.warn('[ACCOUNT] Tenant not yet set – skipping item rendering.')
    return []
  }

  const context = getContext()
  const filteredMatrix = modulesMatrix.filter((item) => item.myAccount === true)
  const visibleItems = getVisibleItems(filteredMatrix)
  // debug
  visibleItems.forEach(item => {
    console.log('[ACCOUNT ITEM DEBUG]', {
      id: item.link?.name || 'unknown',
      tenant: context.tenant,
      roles: context.userRoles,
      visible: true
    })
  })

  return visibleItems
})

</script>

<style scoped lang="scss">
@import "../../styles/_variables.scss";
</style>
